import React, { useState, useEffect } from 'react';
import { departmentsApi, Department, CreateDepartmentData } from '../../lib/api/departments.api';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import Loader from '../../components/common/Loader';

export default function DepartamentosPage() {
    const [departments, setDepartments] = useState<Department[]>([]);
    const [loading, setLoading] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
    const [formData, setFormData] = useState<CreateDepartmentData>({
        name: '',
        description: '',
    });

    // Cargar departamentos al montar el componente
    useEffect(() => {
        loadDepartments();
    }, []);

    const loadDepartments = async () => {
        try {
            setLoading(true);
            const data = await departmentsApi.getDepartments();
            setDepartments(data);
        } catch (error) {
            console.error('Error al cargar departamentos:', error);
            alert('Error al cargar departamentos');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateDepartment = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setLoading(true);
            const data = await departmentsApi.createDepartment(formData);
            setDepartments([...departments, data]);
            setShowCreateModal(false);
            setFormData({ name: '', description: '' });
            alert('Departamento creado correctamente');
        } catch (error) {
            console.error('Error al crear departamento:', error);
            alert('Error al crear departamento');
        } finally {
            setLoading(false);
        }
    };

    const handleUpdateDepartment = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            setLoading(true);
            const data = await departmentsApi.updateDepartment(selectedDepartment!.id, formData);
            setDepartments(departments.map(d => d.id === selectedDepartment!.id ? data : d));
            setShowEditModal(false);
            setSelectedDepartment(null);
            setFormData({ name: '', description: '' });
            alert('Departamento actualizado correctamente');
        } catch (error) {
            console.error('Error al actualizar departamento:', error);
            alert('Error al actualizar departamento');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteDepartment = async () => {
        if (!selectedDepartment) return;

        if (!confirm(`¿Estás seguro de eliminar el departamento "${selectedDepartment.name}"?`)) return;

        try {
            setLoading(true);
            await departmentsApi.deleteDepartment(selectedDepartment.id);
            setDepartments(departments.filter(d => d.id !== selectedDepartment.id));
            setShowDeleteModal(false);
            setSelectedDepartment(null);
            alert('Departamento eliminado correctamente');
        } catch (error) {
            console.error('Error al eliminar departamento:', error);
            alert('Error al eliminar departamento');
        } finally {
            setLoading(false);
        }
    };

    const openCreateModal = () => {
        setFormData({ name: '', description: '' });
        setShowCreateModal(true);
    };

    const openEditModal = (department: Department) => {
        setSelectedDepartment(department);
        setFormData({ name: department.name, description: department.description || '' });
        setShowEditModal(true);
    };

    const closeModals = () => {
        setShowCreateModal(false);
        setShowEditModal(false);
        setShowDeleteModal(false);
        setSelectedDepartment(null);
        setFormData({ name: '', description: '' });
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Departamentos</h1>
                <p className="text-gray-600 dark:text-gray-400">Gestiona los departamentos de tu empresa</p>
            </div>

            {loading ? (
                <Loader />
            ) : (
                <>
                    <div className="mb-6">
                        <Button onClick={openCreateModal} className="bg-blue-600 hover:bg-blue-700 text-white">
                            + Crear Departamento
                        </Button>
                    </div>

                    {departments.length === 0 ? (
                        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                            No hay departamentos creados
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead>
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Nombre
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Descripción
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Empleados
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Estado
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                            Acciones
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {departments.map((department) => (
                                        <tr key={department.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {department.name}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {department.description || '-'}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {department.employeeCount || 0}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {department.active ? (
                                                    <span className="px-2 inline-flex items-center justify-center">
                                                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                                                    </span>
                                                ) : (
                                                    <span className="px-2 inline-flex items-center justify-center">
                                                        <span className="w-2 h-2 rounded-full bg-red-500"></span>
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex gap-2 justify-center">
                                                    <button
                                                        onClick={() => openEditModal(department)}
                                                        className="text-blue-600 hover:text-blue-800"
                                                    >
                                                        Editar
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setSelectedDepartment(department);
                                                            setShowDeleteModal(true);
                                                        }}
                                                        className="text-red-600 hover:text-red-800 ml-2"
                                                    >
                                                        Eliminar
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            {/* Modal de creación */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Crear Departamento</h2>
                        <button
                            onClick={closeModals}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                        >
                            ✕
                        </button>
                        <form onSubmit={handleCreateDepartment}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nombre del departamento *
                                    </label>
                                    <Input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                        className="w-full"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Descripción
                                    </label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:border-blue-500 focus:ring-offset-2"
                                        rows={3}
                                    />
                                </div>
                                <div className="flex justify-end gap-3 mt-6">
                                    <Button
                                        type="submit"
                                        disabled={loading}
                                        className="bg-blue-600 hover:bg-blue-700 text-white"
                                    >
                                        {loading ? 'Creando...' : 'Crear Departamento'}
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={closeModals}
                                        className="bg-gray-200 hover:bg-gray-300 text-gray-700"
                                    >
                                        Cancelar
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de edición */}
            {showEditModal && selectedDepartment && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Editar Departamento</h2>
                        <button
                            onClick={closeModals}
                            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
                        >
                            ✕
                        </button>
                        <form onSubmit={handleUpdateDepartment}>
                            <div className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Nombre del departamento *
                                    </label>
                                    <Input
                                        type="text"
                                        value={formData.name}
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                        className="w-full"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Descripción
                                    </label>
                                    <textarea
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:border-blue-500 focus:ring-offset-2"
                                        rows={3}
                                    />
                                </div>
                                <div className="flex justify-end gap-3 mt-6">
                                    <Button
                                        type="submit"
                                        disabled={loading}
                                        className="bg-blue-600 hover:bg-blue-700 text-white"
                                    >
                                        {loading ? 'Actualizando...' : 'Actualizar Departamento'}
                                    </Button>
                                    <Button
                                        type="button"
                                        onClick={closeModals}
                                        className="bg-gray-200 hover:bg-gray-300 text-gray-700"
                                    >
                                        Cancelar
                                    </Button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal de eliminación */}
            {showDeleteModal && selectedDepartment && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                        <h2 className="text-xl font-bold text-gray-900 mb-4">Eliminar Departamento</h2>
                        <p className="text-gray-600 mb-4">
                            ¿Estás seguro de eliminar el departamento "{selectedDepartment.name}"?
                        </p>
                        <div className="flex justify-end gap-3 mt-6">
                            <Button
                                onClick={() => handleDeleteDepartment()}
                                disabled={loading}
                                className="bg-red-600 hover:bg-red-700 text-white"
                            >
                                {loading ? 'Eliminando...' : 'Eliminar'}
                            </Button>
                            <Button
                                type="button"
                                onClick={closeModals}
                                className="bg-gray-200 hover:bg-gray-300 text-gray-700"
                            >
                                Cancelar
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
